import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { whyChooseUsService } from '../services/whyChooseUsService';
import { useError } from '../context/ErrorContext';
import { CheckCircle2, ShieldCheck, Handshake, Compass } from 'lucide-react';

const icons = {
  'verified-properties': <CheckCircle2 size={24} />,
  'secure-transactions': <ShieldCheck size={24} />,
  'expert-guidance': <Handshake size={24} />,
  'tailored-solutions': <Compass size={24} />,
};

const WhyChooseUs = () => {
  const [pageContent, setPageContent] = useState(null);
  const { showError } = useError();

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const content = await whyChooseUsService.getWhyChooseUsContent();
        setPageContent(content);
      } catch (error) {
        showError('Failed to load Why Choose Us content: ' + error.message, 'error');
      }
    };
    fetchContent();
  }, [showError]);

  if (!pageContent) {
    return (
      <section className="py-20 bg-gray-50 dark:bg-gray-900">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center text-gray-500 dark:text-gray-400">
          <p>Loading content...</p>
        </div>
      </section>
    );
  }

  return (
    <section className="py-24 bg-white dark:bg-gray-950 overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Main Header Section */}
        <div className="text-center mb-16">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ amount: 0.2 }}
            transition={{ duration: 0.6 }}
            className="text-4xl font-display font-bold text-text-primary mb-4"
          >
            {pageContent.title}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ amount: 0.2 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-lg text-gray-600 dark:text-gray-400 max-w-3xl mx-auto"
          >
            {pageContent.subtitle}
          </motion.p>
        </div>

        {/* Features List */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-x-12 gap-y-16">
          {pageContent.features.map((feature, index) => (
            <motion.div
              key={feature.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ amount: 0.2 }}
              transition={{ duration: 0.6, delay: index * 0.15 }}
              className="flex items-start"
            >
              {/* Feature Icon */}
              <div className="flex-shrink-0 w-12 h-12 bg-accent/10 rounded-full flex items-center justify-center text-accent mt-1">
                {icons[feature.id] || <CheckCircle2 size={24} />}
              </div>
              
              {/* Feature Content */}
              <div className="ml-6">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">{feature.title}</h3>
                <p className="text-gray-600 dark:text-gray-400 leading-relaxed">{feature.description}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default WhyChooseUs;